import { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { text } from '../../i18n';
import { navigate } from '../../router';
import { useModelPricingQuery } from '../../queries';
import { useToast } from '../../toast';

export type PaletteDestination = { href: string; label: string };

type Item =
  | { kind: 'page'; key: string; label: string; href: string }
  | { kind: 'model'; key: string; label: string; provider: string };

/**
 * ⌘K / Ctrl+K search: jump to a page, or find a model ID and copy it.
 * Request ID search is intentionally out of scope (PRODUCT.md).
 */
export function CommandPalette({ pages }: { pages: PaletteDestination[] }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [active, setActive] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const { showToast } = useToast();
  const { data: models = [] } = useModelPricingQuery();
  const isMac = typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.platform);

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k') {
        event.preventDefault();
        setOpen((value) => !value);
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, []);

  useEffect(() => {
    if (open) {
      setQuery('');
      setActive(0);
      window.setTimeout(() => inputRef.current?.focus(), 0);
    }
  }, [open]);

  const items = useMemo<Item[]>(() => {
    const q = query.trim().toLowerCase();
    const pageItems: Item[] = pages
      .filter((page) => !q || page.label.toLowerCase().includes(q))
      .map((page) => ({
        kind: 'page',
        key: `page:${page.href}`,
        label: page.label,
        href: page.href,
      }));
    const modelItems: Item[] = q
      ? models
          .filter(
            (model) =>
              model.id.toLowerCase().includes(q) || model.provider.toLowerCase().includes(q),
          )
          .slice(0, 8)
          .map((model) => ({
            kind: 'model',
            key: `model:${model.id}`,
            label: model.id,
            provider: model.provider,
          }))
      : [];
    return [...pageItems, ...modelItems];
  }, [models, pages, query]);

  useEffect(() => {
    setActive((value) => Math.min(value, Math.max(items.length - 1, 0)));
  }, [items.length]);

  async function choose(item: Item | undefined) {
    if (!item) return;
    setOpen(false);
    if (item.kind === 'page') {
      navigate(item.href);
      return;
    }
    try {
      await navigator.clipboard.writeText(item.label);
      showToast(
        text(`已复制 ${item.label}`, `Copied ${item.label}`, `${item.label} をコピーしました`),
      );
    } catch {
      showToast(text('复制失败', 'Copy failed', 'コピーに失敗しました'), { tone: 'error' });
    }
  }

  const firstModelIndex = items.findIndex((item) => item.kind === 'model');

  return (
    <>
      <button className="topbar-search" type="button" onClick={() => setOpen(true)}>
        <svg
          aria-hidden="true"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <circle cx="11" cy="11" r="7" />
          <path d="m20 20-3.5-3.5" />
        </svg>
        <span>{text('搜索页面或模型', 'Search pages or models', 'ページやモデルを検索')}</span>
        <kbd>{isMac ? '⌘K' : 'Ctrl K'}</kbd>
      </button>
      {/* Portal to <body>: the topbar's backdrop-filter would otherwise trap the fixed overlay. */}
      {open
        ? createPortal(
            <div className="palette-backdrop" onMouseDown={() => setOpen(false)}>
              <div
                className="palette"
                role="dialog"
                aria-modal="true"
                aria-label={text('搜索', 'Search', '検索')}
                onMouseDown={(event) => event.stopPropagation()}
              >
                <input
                  ref={inputRef}
                  className="palette-input"
                  value={query}
                  placeholder={text(
                    '输入页面名称或模型 ID…',
                    'Type a page name or model ID…',
                    'ページ名またはモデル ID を入力…',
                  )}
                  onChange={(event) => {
                    setQuery(event.currentTarget.value);
                    setActive(0);
                  }}
                  onKeyDown={(event) => {
                    if (event.key === 'Escape') setOpen(false);
                    if (event.key === 'ArrowDown') {
                      event.preventDefault();
                      setActive((value) => Math.min(value + 1, items.length - 1));
                    }
                    if (event.key === 'ArrowUp') {
                      event.preventDefault();
                      setActive((value) => Math.max(value - 1, 0));
                    }
                    if (event.key === 'Enter') {
                      event.preventDefault();
                      void choose(items[active]);
                    }
                  }}
                />
                <div className="palette-list" role="listbox">
                  {items.length === 0 ? (
                    <div className="palette-empty">
                      {text('没有匹配的结果', 'No matches', '一致する結果はありません')}
                    </div>
                  ) : (
                    items.map((item, index) => (
                      <div key={item.key}>
                        {index === 0 && item.kind === 'page' ? (
                          <div className="palette-group">{text('页面', 'Pages', 'ページ')}</div>
                        ) : null}
                        {index === firstModelIndex ? (
                          <div className="palette-group">
                            {text(
                              '模型（回车复制 ID）',
                              'Models (Enter copies ID)',
                              'モデル（Enter で ID をコピー）',
                            )}
                          </div>
                        ) : null}
                        <button
                          type="button"
                          role="option"
                          aria-selected={index === active}
                          className={`palette-item${index === active ? ' is-active' : ''}`}
                          onMouseEnter={() => setActive(index)}
                          onClick={() => void choose(item)}
                        >
                          <span className={item.kind === 'model' ? 'palette-mono' : ''}>
                            {item.label}
                          </span>
                          {item.kind === 'model' ? (
                            <span className="palette-meta">{item.provider}</span>
                          ) : null}
                        </button>
                      </div>
                    ))
                  )}
                </div>
              </div>
            </div>,
            document.body,
          )
        : null}
    </>
  );
}
